import { z } from 'zod';

// ==================== Report DSL Schemas ====================

// Expression schema for dynamic values
export const ExpressionSchema = z.object({
  $exp: z.string(),
  $deps: z.array(z.string()).optional(),
});

export type Expression = z.infer<typeof ExpressionSchema>;

// Binding schema for data binding
export const BindingSchema = z.object({
  $bind: z.string(),
  $transform: z
    .array(
      z.object({
        name: z.string(),
        args: z.array(z.any()).optional(),
      }),
    )
    .optional(),
});

export type Binding = z.infer<typeof BindingSchema>;

// For directive schema
export const ForDirectiveSchema = z.object({
  in: z.union([ExpressionSchema, BindingSchema, z.string()]),
  as: z.string(),
  key: z.string().optional(),
  index: z.string().optional(),
});

export type ForDirective = z.infer<typeof ForDirectiveSchema>;

// Query specification schema
export const QuerySpecSchema = z.object({
  graphql: z.string().optional(),
  sql: z.string().optional(),
  variables: z.record(z.string(), z.any()).optional(),
  params: z.record(z.string(), z.any()).optional(),
  key: z.string().optional(),
  refetchPolicy: z
    .enum(['cache-first', 'network-only', 'cache-and-network'])
    .optional(),
  dependencies: z.array(z.string()).optional(),
});

export type QuerySpec = z.infer<typeof QuerySpecSchema>;

// UI Element schema
export const UIElementSchema: z.ZodType<any> = z.lazy(() =>
  z.object({
    id: z.string(),
    type: z.string(),
    key: z.union([z.string(), ExpressionSchema]).optional(),
    props: z.record(z.string(), z.any()).optional(),
    query: QuerySpecSchema.optional(),
    if: ExpressionSchema.optional(),
    elseIf: ExpressionSchema.optional(),
    for: ForDirectiveSchema.optional(),
    'link-to': z
      .union([
        z.string(),
        ExpressionSchema,
        BindingSchema,
        z.object({
          ui: z.union([z.string(), ExpressionSchema, BindingSchema]),
          params: z.record(z.string(), z.any()).optional(),
        }),
      ])
      .optional(),
    _meta: z
      .object({
        id: z.string().optional(),
        version: z.string().optional(),
        created: z.string().optional(),
        lastModified: z.string().optional(),
      })
      .optional(),
    children: z.any().optional(),
    else: UIElementSchema.optional(),
    slots: z
      .record(z.string(), z.union([UIElementSchema, z.array(UIElementSchema)]))
      .optional(),
    platform: z
      .object({
        web: z.any().optional(),
        ios: z.any().optional(),
        android: z.any().optional(),
      })
      .optional(),
  })
);

export type UIElement = z.infer<typeof UIElementSchema>;

// UI Component schema
export const UIComponentSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  props: z.record(z.string(), z.any()).optional(),
  states: z.record(z.string(), z.any()).optional(),
  methods: z
    .record(
      z.string(),
      z.object({
        fn: z.string(),
        params: z.record(z.string(), z.any()).optional(),
      }),
    )
    .optional(),
  effects: z
    .array(
      z.object({
        fn: z.string(),
        deps: z.array(z.string()).optional(),
      }),
    )
    .optional(),
  data: z.record(z.string(), z.any()).optional(),
  render: UIElementSchema,
  query: QuerySpecSchema.optional(),
});

export type UIComponent = z.infer<typeof UIComponentSchema>;

// DSL Renderer Props schema
export const DSLRendererPropsSchema = z.object({
  dsl: UIComponentSchema,
  data: z.record(z.string(), z.any()).optional(),
  context: z.record(z.string(), z.any()).optional(),
});

export type DSLRendererProps = z.infer<typeof DSLRendererPropsSchema>;
